/**
 * Jivandhara motion toolkit for the public site.
 *
 * Built on the same idea as <Reveal>: content is visible by default and is
 * only hidden right before it scrolls into view, so nothing is lost if JS or
 * IntersectionObserver is unavailable. All effects animate opacity,
 * transform or clip-path only, and are neutralised by
 * `prefers-reduced-motion` (see the motion section of styles.css).
 */
import {
  Children,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ElementType,
  type ReactElement,
  type ReactNode,
} from "react";
import { CountUp } from "@project/user/components/reveal";
import {
  prefersReducedMotion,
  useRevealVisible,
} from "@project/user/components/use-reveal";

export { Reveal, CountUp } from "@project/user/components/reveal";
export type { RevealVariant } from "@project/user/components/reveal";

/** Counter that animates up to its real value when scrolled into view. */
export const AnimatedCounter = CountUp;

/* ------------------------------------------------------------------ */
/* ImageReveal                                                         */
/* ------------------------------------------------------------------ */

/**
 * Cinematic image reveal: a clip-path wipe in the given direction while the
 * image settles from a slight zoom. Wrap the <img> (or a group of images);
 * pass the sizing classes the image's old wrapper needed via `className`.
 */
export function ImageReveal({
  children,
  direction = "left",
  delay = 0,
  className = "",
  as: As = "div",
}: {
  children: ReactNode;
  direction?: "left" | "right" | "up" | "center";
  delay?: number;
  className?: string;
  as?: ElementType;
}) {
  const ref = useRef<HTMLElement | null>(null);
  const visible = useRevealVisible(ref);
  const style: CSSProperties | undefined = delay
    ? { transitionDelay: `${delay}ms` }
    : undefined;

  return (
    <As
      ref={ref as never}
      data-visible={visible ? "true" : "false"}
      data-dir={direction}
      style={style}
      className={`img-reveal ${className}`}
    >
      <div className="img-reveal__inner" style={style}>
        {children}
      </div>
    </As>
  );
}

/* ------------------------------------------------------------------ */
/* RevealText                                                          */
/* ------------------------------------------------------------------ */

/**
 * Heading/line reveal: the text slides up from behind a mask. Use one per
 * visual line (or per heading). Renders as `as` (default <span>) so it can
 * sit inside an existing <h1>/<h2> without changing its styles.
 */
export function RevealText({
  children,
  delay = 0,
  className = "",
  as: As = "span",
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
  as?: ElementType;
}) {
  const ref = useRef<HTMLElement | null>(null);
  const visible = useRevealVisible(ref);

  return (
    <As
      ref={ref as never}
      data-visible={visible ? "true" : "false"}
      className={`text-mask ${className}`}
    >
      <span
        className="text-mask__line"
        style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      >
        {children}
      </span>
    </As>
  );
}

/* ------------------------------------------------------------------ */
/* Stagger / StaggerItem                                               */
/* ------------------------------------------------------------------ */

const StaggerContext = createContext<{ step: number; base: number }>({
  step: 120,
  base: 0,
});

/**
 * One observer for a whole group; children wrapped in <StaggerItem index>
 * enter one after another (`step` ms apart, after an optional `delay`).
 */
export function Stagger({
  children,
  className = "",
  step = 120,
  delay = 0,
  as: As = "div",
}: {
  children: ReactNode;
  className?: string;
  step?: number;
  delay?: number;
  as?: ElementType;
}) {
  const ref = useRef<HTMLElement | null>(null);
  const visible = useRevealVisible(ref);

  return (
    <StaggerContext.Provider value={{ step, base: delay }}>
      <As
        ref={ref as never}
        data-stagger=""
        data-visible={visible ? "true" : "false"}
        className={className}
      >
        {children}
      </As>
    </StaggerContext.Provider>
  );
}

export function StaggerItem({
  children,
  index = 0,
  from = "up",
  className = "",
  as: As = "div",
}: {
  children: ReactNode;
  index?: number;
  from?: "up" | "left" | "right" | "zoom";
  className?: string;
  as?: ElementType;
}) {
  const { step, base } = useContext(StaggerContext);
  // Cap the cascade so long lists never feel slow.
  const delay = base + Math.min(index, 8) * step;

  return (
    <As
      data-from={from}
      className={`stagger-item ${className}`}
      style={{ "--stagger-delay": `${delay}ms` } as CSSProperties}
    >
      {children}
    </As>
  );
}

/* ------------------------------------------------------------------ */
/* MotionPath                                                          */
/* ------------------------------------------------------------------ */

/**
 * A connector line (for journeys, timelines and process steps) that draws
 * itself when scrolled into view. Position it with `className` exactly like
 * the static line it replaces; `lineClassName` sets its colour.
 */
export function MotionPath({
  orientation = "horizontal",
  className = "",
  lineClassName = "bg-gold/60",
  delay = 0,
}: {
  orientation?: "horizontal" | "vertical";
  className?: string;
  lineClassName?: string;
  delay?: number;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const visible = useRevealVisible(ref);

  return (
    <div
      ref={ref}
      aria-hidden
      data-orientation={orientation}
      data-visible={visible ? "true" : "false"}
      className={`motion-path ${className}`}
    >
      <span
        className={`motion-path__line ${lineClassName}`}
        style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Parallax                                                            */
/* ------------------------------------------------------------------ */

// One shared, rAF-throttled scroll listener for every <Parallax>.
type ParallaxSubscriber = () => void;
const parallaxSubscribers = new Set<ParallaxSubscriber>();
let parallaxFrame = 0;

function onParallaxScroll() {
  if (parallaxFrame) {
    return;
  }
  parallaxFrame = requestAnimationFrame(() => {
    parallaxFrame = 0;
    parallaxSubscribers.forEach(fn => fn());
  });
}

function subscribeParallax(fn: ParallaxSubscriber) {
  if (parallaxSubscribers.size === 0) {
    window.addEventListener("scroll", onParallaxScroll, { passive: true });
    window.addEventListener("resize", onParallaxScroll, { passive: true });
  }
  parallaxSubscribers.add(fn);
  return () => {
    parallaxSubscribers.delete(fn);
    if (parallaxSubscribers.size === 0) {
      window.removeEventListener("scroll", onParallaxScroll);
      window.removeEventListener("resize", onParallaxScroll);
      cancelAnimationFrame(parallaxFrame);
      parallaxFrame = 0;
    }
  };
}

/**
 * Soft parallax for background images / decorative layers. The child drifts
 * a few percent against the scroll. Disabled on small screens and for
 * reduced motion. The wrapper clips, so place it where the image's own
 * container was (absolute inset-0 for backgrounds).
 */
export function Parallax({
  children,
  speed = 0.08,
  className = "",
}: {
  children: ReactNode;
  speed?: number;
  className?: string;
}) {
  const outer = useRef<HTMLDivElement | null>(null);
  const inner = useRef<HTMLDivElement | null>(null);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const ok =
      !prefersReducedMotion() &&
      window.matchMedia?.("(min-width: 768px)").matches;
    setEnabled(Boolean(ok));
  }, []);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const update = () => {
      const o = outer.current;
      const i = inner.current;
      if (!o || !i) {
        return;
      }
      const rect = o.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) {
        return;
      }
      const offset =
        (rect.top + rect.height / 2 - window.innerHeight / 2) * -speed;
      i.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0) scale(1.08)`;
    };
    update();
    return subscribeParallax(update);
  }, [enabled, speed]);

  return (
    <div ref={outer} className={`overflow-hidden ${className}`}>
      <div
        ref={inner}
        className="h-full w-full will-change-transform"
        style={enabled ? { transform: "scale(1.08)" } : undefined}
      >
        {children}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* StackCards                                                          */
/* ------------------------------------------------------------------ */

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

// Each pinned card sits this much lower than the one before it, so a strip
// of every earlier card stays visible above the newest one.
const STACK_STEP = 20;
// How much a card shrinks for each later card that slides over it.
const STACK_SHRINK = 0.015;

/**
 * Stacking cards (desktop): every card pins at 15vh and the next one scrolls
 * up and covers it, landing 20px lower so the earlier card's top edge peeks
 * out above. Cards that have been covered shrink very slightly from their
 * centre as later cards pass over them. On phones and with reduced motion
 * the cards simply sit one under the other. The first card's height is set
 * as `--stack-card-h` on the parent element for aligning content beside it.
 */
export function StackCards({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  const items = Children.toArray(children);
  const cards = useRef<(HTMLDivElement | null)[]>([]);
  const [enabled, setEnabled] = useState(false);
  const count = items.length;

  useEffect(() => {
    const mq = window.matchMedia?.("(min-width: 1024px)");
    const sync = () =>
      setEnabled(Boolean(mq?.matches) && !prefersReducedMotion());
    sync();
    mq?.addEventListener("change", sync);
    return () => mq?.removeEventListener("change", sync);
  }, []);

  // Publish the first card's height as --stack-card-h on the parent, so
  // content beside the stack (e.g. a pinned intro) can centre itself on it.
  useEffect(() => {
    const first = cards.current[0];
    const host = first?.parentElement?.parentElement;
    if (!first || !host || typeof ResizeObserver === "undefined") {
      return;
    }
    const ro = new ResizeObserver(() =>
      host.style.setProperty("--stack-card-h", `${first.offsetHeight}px`)
    );
    ro.observe(first);
    return () => {
      ro.disconnect();
      host.style.removeProperty("--stack-card-h");
    };
  }, [count]);

  useEffect(() => {
    const els = cards.current.slice(0, count);
    if (!enabled) {
      els.forEach(el => el && (el.style.transform = ""));
      return;
    }
    const update = () => {
      const vh = window.innerHeight;
      const pin = vh * 0.15;
      // 0 → 1 as each card travels from the bottom of the screen to its pin.
      const arrived = els.map((el, i) => {
        if (!el) {
          return 0;
        }
        const top = el.getBoundingClientRect().top - i * STACK_STEP;
        return clamp01((vh - top) / (vh - pin));
      });
      els.forEach((el, i) => {
        if (!el) {
          return;
        }
        const covered = arrived.slice(i + 1).reduce((sum, p) => sum + p, 0);
        const scale = 1 - STACK_SHRINK * covered;
        el.style.transform = `translate3d(0, ${i * STACK_STEP}px, 0) scale(${scale.toFixed(4)})`;
      });
    };
    update();
    return subscribeParallax(update);
  }, [enabled, count]);

  return (
    <div className={`flex flex-col gap-5 lg:pb-40 ${className}`}>
      {items.map((child, i) => (
        <div
          // Children.toArray gives every element a stable key
          key={(child as ReactElement).key ?? undefined}
          ref={el => {
            cards.current[i] = el;
          }}
          className="origin-center lg:sticky lg:top-[15vh]"
          style={{ zIndex: i + 1 }}
        >
          {child}
        </div>
      ))}
    </div>
  );
}
